// @ts-nocheck
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { TrackedSet } from 'tracked-built-ins'
import Logger, { type LogEntry } from 'customer-flows/utils/logger'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import Icon from 'customer-flows/components/ui-kit/icon'
import ValueInspector from './value-inspector'
import { service } from '@ember/service'

const logBadgeColorMap: { [key: string]: string } = {
  debug: 'log-badge-debug',
  info: 'log-badge-info',
  warn: 'log-badge-warn',
  error: 'log-badge-error'
}

const logCardColorMap: { [key: string]: string } = {
  debug: 'log-card-debug',
  info: 'log-card-info',
  warn: 'log-card-warn',
  error: 'log-card-error'
}

export default class LogInspectorComponent extends Component {
  @service session: Session

  @tracked selectedLogLevels: string[] = ['debug', 'info', 'warn', 'error']

  constructor() {
    super(...arguments)
    this.selectedLogLevels = this.session.restore()['debugToolbarLogLevels'] ?? ['debug', 'info', 'warn', 'error']
  }

  get logs() {
    return Logger.logs.filter((log) => this.selectedLogLevels.includes(log.level))
  }

  @action
  toggleLogLevel(level: string) {
    if (this.selectedLogLevels.includes(level)) {
      this.selectedLogLevels = [...this.selectedLogLevels.filter((l) => l !== level)]
    } else {
      this.selectedLogLevels = [...this.selectedLogLevels, level]
    }

    this.session.persist({ debugToolbarLogLevels: this.selectedLogLevels })
  }

  getBadgeColorClasses = (level: string) => {
    return logBadgeColorMap[level]
  }

  getCardColorClasses = (level: string) => {
    return logCardColorMap[level]
  }

  formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  }

  get reversedLogs() {
    return [...this.logs].reverse()
  }

  isLevelToggled = (level: string) => {
    return this.selectedLogLevels.includes(level)
  }

  get debugStyle() {
    if (this.selectedLogLevels.includes('debug')) {
      return 'border-color: var(--log-debug-300); background-color: var(--log-debug-200); color: var(--log-debug-700);'
    }
    return 'border-color: #e5e7eb; background-color: #f9fafb; color: #9ca3af;'
  }

  get infoStyle() {
    if (this.selectedLogLevels.includes('info')) {
      return 'border-color: var(--log-info-300); background-color: var(--log-info-200); color: var(--log-info-700);'
    }
    return 'border-color: #e5e7eb; background-color: #f9fafb; color: #9ca3af;'
  }

  get warnStyle() {
    if (this.selectedLogLevels.includes('warn')) {
      return 'border-color: var(--log-warn-300); background-color: var(--log-warn-200); color: var(--log-warn-700);'
    }
    return 'border-color: #e5e7eb; background-color: #f9fafb; color: #9ca3af;'
  }

  get errorStyle() {
    if (this.selectedLogLevels.includes('error')) {
      return 'border-color: var(--log-error-300); background-color: var(--log-error-200); color: var(--log-error-700);'
    }
    return 'border-color: #e5e7eb; background-color: #f9fafb; color: #9ca3af;'
  }

  expandedLogs = new TrackedSet<LogEntry>()

  @action
  togglePayload(log: LogEntry) {
    if (this.expandedLogs.has(log)) {
      this.expandedLogs.delete(log)
    } else {
      this.expandedLogs.add(log)
    }
  }

  isPayloadExpanded = (log: LogEntry) => {
    return this.expandedLogs.has(log)
  }

  /**
   * Merges all plain objects from log.info into a single payload object.
   * Skips raw Error instances — their data is already in the serialized keys
   * added by serializeError().
   */
  getPayload = (log: LogEntry): Record<string, unknown> | null => {
    if (!log.info?.length) return null

    const merged: Record<string, unknown> = {}
    for (const item of log.info) {
      if (item && typeof item === 'object' && !(item instanceof Error)) {
        Object.assign(merged, item)
      }
    }

    return Object.keys(merged).length > 0 ? merged : null
  }


  @action
  clearLogs() {
    Logger.logs = []
  }

  <template>
    <div data-testid='log-inspector' class='flex h-full w-full flex-col p-4'>
      <div class='mb-3 flex items-center justify-between'>
        <h2 class='text-xbold-para-16 text-neutrals-400'>Logs</h2>
        <button
          class='rounded-3 text-reg-para-12 flex cursor-pointer items-center gap-1 border border-neutrals-200 bg-neutrals-200 px-3 py-1.5 text-neutrals-400 transition-colors hover:border-theme-300 hover:bg-theme-100 hover:text-theme-300'
          {{on 'click' this.clearLogs}}
        >
          <Icon @src='delete' class='h-3 w-3' />
          Clear
        </button>
      </div>

      <div class='mb-3 flex flex-wrap gap-1.5'>
        <button
          type='button'
          class='rounded-3 text-reg-para-12 cursor-pointer border px-3 py-1.5 transition-colors'
          style={{this.debugStyle}}
          {{on 'click' (fn this.toggleLogLevel 'debug')}}
        >debug</button>
        <button
          type='button'
          class='rounded-3 text-reg-para-12 cursor-pointer border px-3 py-1.5 transition-colors'
          style={{this.infoStyle}}
          {{on 'click' (fn this.toggleLogLevel 'info')}}
        >info</button>
        <button
          type='button'
          class='rounded-3 text-reg-para-12 cursor-pointer border px-3 py-1.5 transition-colors'
          style={{this.warnStyle}}
          {{on 'click' (fn this.toggleLogLevel 'warn')}}
        >warn</button>
        <button
          type='button'
          class='rounded-3 text-reg-para-12 cursor-pointer border px-3 py-1.5 transition-colors'
          style={{this.errorStyle}}
          {{on 'click' (fn this.toggleLogLevel 'error')}}
        >error</button>
      </div>

      <div class='mb-2 flex items-center gap-2 rounded-3 border border-neutrals-200 bg-neutrals-100 px-3 py-2'>
        <span class='text-reg-para-12 text-neutrals-300'>Newest logs appear at the top</span>
      </div>

      <div
        data-testid='log-inspector__logs'
        class='flex flex-1 flex-col gap-3 overflow-auto'
      >
        {{#each this.reversedLogs as |log|}}
          {{#let (this.getPayload log) as |payload|}}
            <div class='rounded-3 flex flex-col gap-2 border p-3 {{this.getCardColorClasses log.level}}'>
              <div class='flex items-center justify-between gap-2'>
                <div class='flex items-center gap-2'>
                  <div class='text-reg-para-12 rounded-3 shrink-0 border px-2 py-1 {{this.getBadgeColorClasses log.level}}'>
                    {{log.level}}
                  </div>
                  <div class='text-reg-para-12 rounded-2 border border-neutrals-200 bg-neutrals-0 px-2 py-0.5 font-mono text-neutrals-400'>
                    {{this.formatTimestamp log.timestamp}}
                  </div>
                </div>
                <div class='text-reg-para-10 shrink-0 text-neutrals-300'>{{log.callerTrace}}</div>
              </div>
              <div class='flex items-start justify-between gap-2'>
                <div class='text-reg-para-14 break-words text-neutrals-400'>{{log.message}}</div>
                {{#if payload}}
                  <button
                    type='button'
                    class='text-reg-para-10 inline-flex shrink-0 cursor-pointer select-none items-center gap-1 rounded-full border border-neutrals-300/30 px-2 py-0.5 font-mono text-neutrals-400 transition-colors hover:border-neutrals-400/50 hover:text-neutrals-500'
                    {{on 'click' (fn this.togglePayload log)}}
                  >
                    <span class='text-[10px] leading-none'>{{if (this.isPayloadExpanded log) '▾' '▸'}}</span>
                    payload
                  </button>
                {{/if}}
              </div>
              {{#if (this.isPayloadExpanded log)}}
                {{#if payload}}
                  <div class='rounded-2 flex flex-col gap-2 bg-neutrals-50 p-3'>
                    {{#each-in payload as |key value|}}
                      <div class='flex flex-col gap-1'>
                        <span class='text-bold-para-12 text-neutrals-400'>{{key}}</span>
                        <ValueInspector @value={{value}} />
                      </div>
                    {{/each-in}}
                  </div>
                {{/if}}
              {{/if}}
            </div>
          {{/let}}
        {{/each}}
      </div>

      <style>
        .log-badge-debug {
          background-color: var(--log-debug-100);
          border-color: var(--log-debug-200);
          color: var(--log-debug-700);
        }

        .log-badge-info {
          background-color: var(--log-info-100);
          border-color: var(--log-info-200);
          color: var(--log-info-700);
        }

        .log-badge-warn {
          background-color: var(--log-warn-100);
          border-color: var(--log-warn-200);
          color: var(--log-warn-700);
        }

        .log-badge-error {
          background-color: var(--log-error-100);
          border-color: var(--log-error-200);
          color: var(--log-error-700);
        }

        .log-card-debug {
          border-color: var(--log-debug-100);
          background-color: var(--log-debug-50);
        }

        .log-card-info {
          border-color: var(--log-info-100);
          background-color: var(--log-info-50);
        }

        .log-card-warn {
          border-color: var(--log-warn-100);
          background-color: var(--log-warn-50);
        }

        .log-card-error {
          border-color: var(--log-error-100);
          background-color: var(--log-error-50);
        }

        .log-button-inactive {
          border-color: #e5e7eb;
          background-color: #f9fafb;
          color: #9ca3af;
        }

        .log-button-debug-active {
          border-color: var(--log-debug-200);
          background-color: var(--log-debug-100);
          color: var(--log-debug-700);
        }

        .log-button-debug-hover:hover {
          border-color: var(--log-debug-200);
          background-color: var(--log-debug-100);
        }

        .log-button-info-active {
          border-color: var(--log-info-300);
          background-color: var(--log-info-200);
          color: var(--log-info-700);
        }

        .log-button-info-hover:hover {
          border-color: var(--log-info-200);
          background-color: var(--log-info-100);
        }

        .log-button-warn-active {
          border-color: var(--log-warn-300);
          background-color: var(--log-warn-200);
          color: var(--log-warn-700);
        }

        .log-button-warn-hover:hover {
          border-color: var(--log-warn-200);
          background-color: var(--log-warn-100);
        }

        .log-button-error-active {
          border-color: var(--log-error-300);
          background-color: var(--log-error-200);
          color: var(--log-error-700);
        }

        .log-button-error-hover:hover {
          border-color: var(--log-error-200);
          background-color: var(--log-error-100);
        }
      </style>
    </div>
  </template>
}
